import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import { ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import { type FC, type ReactNode } from 'react';

import * as entityStyles from './settings-entity.css';

export const SettingsEntityCard: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <article className={entityStyles.card}>{children}</article>;
};

export const SettingsEntityList: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <div className={entityStyles.list}>{children}</div>;
};

export const SettingsEntitySummary: FC<{
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  dataTestId?: string;
}> = ({
  leading,
  title,
  subtitle,
  badges,
  meta,
  aside,
  expanded,
  onToggle,
  dataTestId,
}) => {
  return (
    <div
      className={entityStyles.summary}
      onClick={onToggle}
      data-testid={dataTestId}
    >
      <div className={entityStyles.summaryMain}>
        <div className={entityStyles.titleRow}>
          <ArrowRightSmallIcon
            className={entityStyles.expandIcon}
            style={{
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
          {leading}
          <div className={entityStyles.titleBlock}>
            <div className={entityStyles.title}>{title}</div>
            {subtitle ? (
              <div className={entityStyles.subtitle}>{subtitle}</div>
            ) : null}
          </div>
          {badges ? (
            <div className={entityStyles.badgeRow}>{badges}</div>
          ) : null}
        </div>

        {meta ? <div className={entityStyles.meta}>{meta}</div> : null}
      </div>
      {aside ? <div className={entityStyles.summaryAside}>{aside}</div> : null}
    </div>
  );
};

export const SettingsEntityMessage: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <div className={entityStyles.message}>{children}</div>;
};

export const SettingsEntityAvatar: FC<{
  size?: number;
  userId?: string | null;
  name?: string | null;
  url?: string | null;
  fallbackName?: string;
}> = ({ size = 28, userId, name, url, fallbackName = 'User' }) => {
  return (
    <CachedUserAvatar
      size={size}
      userId={userId}
      name={name || fallbackName}
      url={url ?? null}
    />
  );
};

export const SettingsExpandableItem: FC<{
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  dataTestId?: string;
  children?: ReactNode;
}> = ({
  leading,
  title,
  subtitle,
  badges,
  meta,
  aside,
  expanded,
  onToggle,
  dataTestId,
  children,
}) => {
  return (
    <SettingsEntityCard>
      <SettingsEntitySummary
        leading={leading}
        title={title}
        subtitle={subtitle}
        badges={badges}
        meta={meta}
        aside={aside}
        expanded={expanded}
        onToggle={onToggle}
        dataTestId={dataTestId}
      />
      {expanded && children ? (
        <div className={entityStyles.expanded}>{children}</div>
      ) : null}
    </SettingsEntityCard>
  );
};

export const SettingsEntityIdentityAside: FC<{
  avatar: ReactNode;
  name: ReactNode;
}> = ({ avatar, name }) => {
  return (
    <div className={entityStyles.identityAside}>
      {avatar}
      <span className={entityStyles.identityName}>{name}</span>
    </div>
  );
};

export const SettingsBadge: FC<{
  variant?: 'soft' | 'active' | 'error';
  children: ReactNode;
}> = ({ variant = 'soft', children }) => {
  const className =
    variant === 'active'
      ? entityStyles.activeBadge
      : variant === 'error'
        ? entityStyles.errorBadge
        : entityStyles.softBadge;
  return <span className={className}>{children}</span>;
};

export const SettingsMetaItem: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <span className={entityStyles.metaItem}>{children}</span>;
};
